import React from 'react';
import BaseModal from '../base/BaseModal';
import { formatDateShort, formatDate } from '../../utils/formatDate';
import { fixMojibake } from '../../utils/downLoadUtils';
import { Globe, ClipboardList, Bot, Eye, Download, Image as ImageIcon, Tag, AlertCircle } from 'lucide-react';

const STATUS_MAP = {
    NHAP: { label: 'Nháp', bg: 'bg-gray-100 text-gray-700 border-gray-200' },
    CHO_DUYET: { label: 'Chờ duyệt', bg: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    DA_DUYET: { label: 'Đã duyệt', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    TU_CHOI: { label: 'Từ chối', bg: 'bg-red-50 text-red-700 border-red-200' },
    LUU_TRU: { label: 'Lưu trữ', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const PHAM_VI_MAP = {
    CONG_KHAI: { label: 'Công khai' },
    NOI_BO: { label: 'Nội bộ' },
    HAN_CHE: { label: 'Hạn chế' },
};

function formatFileSize(bytes) {
    if (!bytes && bytes !== 0) return '---';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentDetailModal({ isOpen, onClose, document, loading = false }) {
    if (!document && !loading) return null;

    const getFullFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('blob:') || path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        const base = (process.env.REACT_APP_API_URL || 'http://localhost:8880').replace(/\/+$/, '');
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${base}${cleanPath}`;
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title="Chi tiết tài liệu"
            size="3xl"
        >
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                    <span className="ml-3 text-gray-500">Đang tải chi tiết...</span>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Header Title & Subtitle */}
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 leading-snug">
                            {document?.tieu_de || '---'}
                        </h2>
                        {document?.mo_ta && (
                            <p className="text-sm text-gray-600 mt-1">
                                {document.mo_ta}
                            </p>
                        )}
                    </div>

                    {/* Rejection Alert if rejected */}
                    {(document?.trang_thai === 'TU_CHOI' || document?.ly_do_tu_choi) && (
                        <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs sm:text-sm text-red-800 flex items-start gap-3 shadow-xs">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <span className="font-bold text-red-900 block mb-0.5">Lý do từ chối:</span>
                                <span className="text-red-800 leading-relaxed">{document.ly_do_tu_choi || 'Không có lý do cụ thể'}</span>
                            </div>
                        </div>
                    )}

                    {/* THÔNG TIN CHUNG */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                            THÔNG TIN CHUNG
                        </h3>

                        {/* Status Badges Row */}
                        <div className="flex items-center justify-between gap-4 flex-wrap text-sm text-gray-700 font-medium">
                            <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-blue-600" />
                                <span>Phạm vi:</span>
                                <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                    {PHAM_VI_MAP[document?.pham_vi]?.label || document?.pham_vi || 'Công khai'}
                                </span>
                            </div>

                            <div className="flex items-center gap-2">
                                <ClipboardList className="w-4 h-4 text-red-600" />
                                <span>Trạng thái:</span>
                                <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold ${STATUS_MAP[document?.trang_thai]?.bg || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                                    {STATUS_MAP[document?.trang_thai]?.label || document?.trang_thai || 'Chờ duyệt'}
                                </span>
                            </div>

                            <div className="flex items-center gap-2">
                                <Bot className="w-4 h-4 text-indigo-600" />
                                <span>Trợ lý AI:</span>
                                <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold ${document?.ai_da_hoc ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                    {document?.ai_da_hoc ? 'Đã học' : 'Chưa học'}
                                </span>
                            </div>
                        </div>

                        {/* 2 Column Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 text-sm sm:text-base">
                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-normal">Tên di tích</span>
                                    <span className="text-gray-900 font-semibold truncate max-w-[220px]" title={document?.ten_di_tich}>
                                        {document?.ten_di_tich || '-'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-normal">Loại văn bản</span>
                                    <span className="text-gray-900 font-semibold">{document?.thu_vien_danh_muc?.ten || '-'}</span>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-normal">Địa chỉ</span>
                                    <span className="text-gray-900 font-semibold truncate max-w-[220px]" title={document?.dia_chi}>
                                        {document?.dia_chi || '-'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-normal">Ngày ban hành</span>
                                    <span className="text-gray-900 font-semibold">
                                        {document?.ngay_ban_hanh ? formatDateShort(document.ngay_ban_hanh) : '-'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-100" />

                    {/* THỐNG KÊ */}
                    <div className="space-y-2">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            THỐNG KÊ
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                            <div className="bg-slate-50/80 rounded-lg px-3 py-2 flex items-center justify-between border border-slate-100">
                                <div className="flex items-center gap-2">
                                    <Eye className="w-4 h-4 text-blue-500" />
                                    <span className="text-xs text-gray-600 font-medium">Lượt xem</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{(document?.luot_xem ?? 0).toLocaleString()}</span>
                            </div>
                            <div className="bg-slate-50/80 rounded-lg px-3 py-2 flex items-center justify-between border border-slate-100">
                                <div className="flex items-center gap-2">
                                    <Download className="w-4 h-4 text-emerald-500" />
                                    <span className="text-xs text-gray-600 font-medium">Lượt tải</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{(document?.so_luot_tai ?? 0).toLocaleString()}</span>
                            </div>
                            <div className="bg-slate-50/80 rounded-lg px-3 py-2 flex items-center justify-between border border-slate-100">
                                <div className="flex items-center gap-2">
                                    <ImageIcon className="w-4 h-4 text-indigo-500" />
                                    <span className="text-xs text-gray-600 font-medium">Media</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{document?._count?.thu_vien_tai_lieu_media ?? (document?.thu_vien_tai_lieu_media?.length ?? 0)}</span>
                            </div>
                            <div className="bg-slate-50/80 rounded-lg px-3 py-2 flex items-center justify-between border border-slate-100">
                                <div className="flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-amber-500" />
                                    <span className="text-xs text-gray-600 font-medium">Tags</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{document?.thu_vien_tai_lieu_tag?.length ?? 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* Tags */}
                    {document?.thu_vien_tai_lieu_tag?.length > 0 && (
                        <>
                            <hr className="border-gray-100" />
                            <div className="space-y-2">
                                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Tags</h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {document.thu_vien_tai_lieu_tag.map((item, idx) => (
                                        <span key={item.thu_vien_tag?.id || idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-indigo-100 text-indigo-800">
                                            {item.thu_vien_tag?.ten || '---'}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* File tài liệu */}
                    {document?.thu_vien_tai_lieu_file?.length > 0 && (
                        <>
                            <hr className="border-gray-100" />
                            <div className="space-y-2">
                                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">File tài liệu</h3>
                                <div className="space-y-2">
                                    {document.thu_vien_tai_lieu_file.map((file) => (
                                        <div key={file.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{fixMojibake(file.ten_file)}</p>
                                                    <p className="text-xs sm:text-sm text-gray-500">{file.kich_thuoc_mb ? `${file.kich_thuoc_mb} MB` : '---'}</p>
                                                </div>
                                            </div>
                                            <a
                                                href={getFullFileUrl(file.duong_dan)}
                                                download={fixMojibake(file.ten_file)}
                                                className="flex-shrink-0 text-blue-600 hover:text-blue-800 p-1.5 rounded hover:bg-blue-50 transition-colors"
                                                title="Tải xuống"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Media (hình ảnh / video) */}
                    {document?.thu_vien_tai_lieu_media?.length > 0 && (
                        <>
                            <hr className="border-gray-100" />
                            <div className="space-y-2">
                                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Media</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {document.thu_vien_tai_lieu_media.map((media) => (
                                        <div key={media.id} className="relative group bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                                            {media.loai === 'IMAGE' ? (
                                                <img
                                                    src={getFullFileUrl(media.url || media.duong_dan)}
                                                    alt={media.ten_file_goc || 'Image'}
                                                    className="w-full h-32 object-cover"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : media.loai === 'VIDEO' ? (
                                                <div className="relative w-full h-32 bg-black flex items-center justify-center">
                                                    <video
                                                        src={getFullFileUrl(media.url || media.duong_dan)}
                                                        className="w-full h-full object-cover"
                                                        controls
                                                    />
                                                </div>
                                            ) : (
                                                <div className="w-full h-32 flex items-center justify-center bg-gray-100">
                                                    <span className="text-xs sm:text-sm text-gray-500">{media.ten_file_goc || 'File media'}</span>
                                                </div>
                                            )}
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                                                <p className="text-xs sm:text-sm text-white truncate">{media.ten_file_goc || '---'}</p>
                                                <p className="text-xs text-white/70">{formatFileSize(media.kich_thuoc)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Thông tin tạo & phê duyệt */}
                    <hr className="border-gray-100" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1.5 text-xs sm:text-sm text-gray-600">
                        <div>Người tạo: <span className="font-medium text-gray-800">{document?.ten_nguoi_tao || '---'}</span></div>
                        <div>Người duyệt: <span className="font-medium text-gray-800">{document?.ten_nguoi_duyet || '---'}</span></div>
                        <div>Ngày tạo: <span className="font-medium text-gray-800">{document?.thoi_gian_tao ? formatDate(document.thoi_gian_tao) : '---'}</span></div>
                        <div>Thời gian duyệt: <span className="font-medium text-gray-800">{document?.thoi_gian_duyet ? formatDate(document.thoi_gian_duyet) : '---'}</span></div>
                    </div>
                </div>
            )}
        </BaseModal>
    );
}